import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { Config } from '../../config';
import offensiveApi from '../../services/offensiveApi';
import useOffensiveStore from '../../stores/offensiveStore';
import CreateTestModal from './CreateTestModal';

const EngagementManager = ({ onEngagementSelect, onEngagementStart }) => {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEngagement, setEditingEngagement] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [subservices, setSubservices] = useState([]);
  const [userRole, setUserRole] = useState(null);

  const { currentEngagement, setCurrentEngagement } = useOffensiveStore();

  // Load engagements, customers, subservices on mount
  useEffect(() => {
    loadEngagements();

    try {
      setUserRole(JSON.parse(localStorage.getItem('role')));
    } catch { /* ignore */ }

    const token = localStorage.getItem('token');
    const headers = { access_token: `Bearer ${token}` };

    axios.get(`${Config.APIBaseURL}/subservices/`, { headers, withCredentials: true })
      .then(res => setSubservices(res.data.filter(s => [1, 2, 3, 4].includes(s.id))))
      .catch(() => setSubservices([
        { id: 1, name: 'Red Teaming', description: 'Full adversary simulation' },
        { id: 2, name: 'Network Intrusion Test', description: 'Traditional pentesting' },
        { id: 3, name: 'DAST', description: 'Dynamic application security testing' },
        { id: 4, name: 'SAST', description: 'Static application security testing' },
      ]));

    axios.get(`${Config.APIBaseURL}/customers/`, { headers, withCredentials: true })
      .then(res => {
        if (Array.isArray(res.data)) {
          setCustomers(res.data.map(c => ({ id: c.id, label: c.short_name })));
        }
      })
      .catch(() => {});
  }, []);

  const loadEngagements = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await offensiveApi.getEngagements();
      setEngagements(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading engagements:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (engagement) => {
    setEditingEngagement(engagement);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingEngagement(null);
  };

  const handleModalCreated = () => {
    loadEngagements();
  };

  const handleDeleteEngagement = async (engagementId) => {
    if (!window.confirm('Are you sure you want to delete this engagement?')) {
      return;
    }

    try {
      setLoading(true);
      await offensiveApi.deleteEngagement(engagementId);
      setEngagements(prev => prev.filter(eng => eng.id !== engagementId));
      
      // Clear current engagement if it was deleted
      if (currentEngagement?.id === engagementId) {
        setCurrentEngagement(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEngagement = async (engagement) => {
    try {
      setLoading(true);
      await offensiveApi.startEngagement(engagement.id);
      setCurrentEngagement(engagement);
      onEngagementSelect?.(engagement);
      onEngagementStart?.(engagement);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEngagement = (engagement) => {
    setCurrentEngagement(engagement);
    onEngagementSelect?.(engagement);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'failed': return 'error';
      case 'stopped': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return <PlayIcon />;
      case 'paused': return <PauseIcon />;
      case 'completed': return <StopIcon />;
      case 'failed': return <StopIcon />;
      case 'stopped': return <StopIcon />;
      default: return <SecurityIcon />;
    }
  };

  if (loading && engagements.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Offensive Engagements
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingEngagement(null); setModalOpen(true); }}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            }
          }}
        >
          New Engagement
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Engagements List */}
      <Grid container spacing={2}>
        {engagements.map((engagement) => (
          <Grid item xs={12} md={6} lg={4} key={engagement.id}>
            <Card 
              sx={{ 
                height: '100%',
                border: currentEngagement?.id === engagement.id ? '2px solid #667eea' : '1px solid #e0e0e0',
                '&:hover': {
                  boxShadow: 4,
                }
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="h3" noWrap>
                    {engagement.name}
                  </Typography>
                  <Chip
                    icon={getStatusIcon(engagement.status)}
                    label={engagement.status?.toUpperCase() || 'DRAFT'}
                    color={getStatusColor(engagement.status)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" mb={2}>
                  {engagement.description || 'No description provided'}
                </Typography>

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Target: {engagement.target || 'Not specified'}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Mode: {engagement.mode || 'autonomous'}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(engagement.created_at).toLocaleDateString()}
                  </Typography>
                </Box>

                {/* Progress Bar */}
                {engagement.progress !== undefined && (
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption">Progress</Typography>
                      <Typography variant="caption">{engagement.progress}%</Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 4,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${engagement.progress}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  onClick={() => handleSelectEngagement(engagement)}
                  disabled={currentEngagement?.id === engagement.id}
                >
                  {currentEngagement?.id === engagement.id ? 'Selected' : 'Select'}
                </Button>
                
                {engagement.status === 'draft' && (
                  <Button
                    size="small"
                    startIcon={<PlayIcon />}
                    onClick={() => handleStartEngagement(engagement)}
                    disabled={loading}
                  >
                    Start
                  </Button>
                )}

                <Tooltip title={engagement.status === 'in progress' ? 'Cannot edit a running engagement' : 'Edit engagement'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(engagement)}
                      disabled={loading || engagement.status === 'in progress'}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <IconButton
                  size="small"
                  onClick={() => handleDeleteEngagement(engagement.id)}
                  disabled={loading || engagement.status === 'running'}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {engagements.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Engagements Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Create your first offensive engagement to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditingEngagement(null); setModalOpen(true); }}
          >
            Create Engagement
          </Button>
        </Box>
      )}

      <CreateTestModal
        open={modalOpen}
        onClose={handleModalClose}
        onCreated={handleModalCreated}
        onNotify={(msg, type) => { if (type === 'error') setError(msg); }}
        userRole={userRole}
        customers={customers || []}
        subservices={subservices || []}
        engagement={editingEngagement}
      />
    </Box>
  );
};

export default EngagementManager;
